/* Renders every banner source in order. Sources: ./core.tsx plus src/features/<area>/banners.tsx. */
import { useState } from 'react'
import { useLocation } from 'react-router'
import { Banner } from '@/components/ui'
import { coreBanners } from './core'
import type { BannerSource } from './registry'

const discovered = import.meta.glob<{ banners?: BannerSource[] }>('/src/features/*/banners.tsx', {
  eager: true,
})
const SOURCES: BannerSource[] = [
  ...coreBanners,
  ...Object.values(discovered).flatMap((m) => m.banners ?? []),
].sort((a, b) => a.order - b.order)

const KEY = 'bpanel.banners.dismissed'
function readDismissed(): string[] {
  try {
    return JSON.parse(sessionStorage.getItem(KEY) ?? '[]') as string[]
  } catch {
    return []
  }
}

function Source({
  source,
  dismissed,
  onDismiss,
}: {
  source: BannerSource
  dismissed: string[]
  onDismiss: (id: string) => void
}) {
  const list = source.useBanners().filter((b) => !dismissed.includes(b.id))
  return (
    <>
      {list.map((b) => (
        <Banner
          key={b.id}
          tone={b.tone}
          icon={b.icon}
          action={b.action}
          onDismiss={b.dismissible ? () => onDismiss(b.id) : undefined}
        >
          {b.content}
        </Banner>
      ))}
    </>
  )
}

export function Banners() {
  const { pathname } = useLocation()
  const path = pathname.replace(/\/+$/, '') || '/'
  const [dismissed, setDismissed] = useState(readDismissed)
  const dismiss = (id: string) => {
    const next = [...dismissed, id]
    setDismissed(next)
    sessionStorage.setItem(KEY, JSON.stringify(next))
  }
  return (
    <div className="flex flex-col gap-2 empty:hidden">
      {SOURCES.filter((s) => !s.hiddenOn?.includes(path)).map((s) => (
        <Source key={s.id} source={s} dismissed={dismissed} onDismiss={dismiss} />
      ))}
    </div>
  )
}
